if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainProductInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        this.mainSubmitButton = document.getElementById(this.dataset.mainSubmitId);
        this.form = this.querySelector('form');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.quantityInput = this.form?.querySelector('[data-sticky-quantity]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.price = this.querySelector('[data-sticky-price]');
        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option]'));
        this.closeButton = this.querySelector('[data-sticky-close]');
        this.variants = this.parseVariants();
        this.lastScrollY = Math.max(0, window.scrollY);
        this.scrollDirection = 'none';
        this.scrollFrame = null;
        this.isDismissed = false;

        if (!this.form || !this.variantInput || !this.submitButton || !this.mainSubmitButton) return;

        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.addEventListener('change', this.handleOptionChange);
        this.closeButton?.addEventListener('click', this.handleClose);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.sectionId) return;
            this.setVariantById(data.variant?.id);
          });
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.hide());
        }

        this.syncInitialSelection();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleOptionChange);
        this.closeButton?.removeEventListener('click', this.handleClose);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('Could not read sticky product variants.', error);
          return [];
        }
      }

      syncInitialSelection() {
        const mainVariantInput = this.mainProductInfo?.querySelector(
          `#product-form-${CSS.escape(this.sectionId)} input[name="id"]`
        );

        if (mainVariantInput?.value && !mainVariantInput.disabled) {
          this.setVariantById(mainVariantInput.value);
          return;
        }

        const sizeSelect = this.optionSelects.find((select) => /size/i.test(select.dataset.optionName || ''));
        if (sizeSelect?.querySelector('[data-size-placeholder]')) sizeSelect.value = '';
        this.updateVariantFromOptions();
      }

      handleOptionChange(event) {
        if (!event.target.matches('[data-sticky-option]')) return;
        const variant = this.updateVariantFromOptions();
        if (variant) this.syncMainProductPicker(variant.options);
      }

      updateVariantFromOptions() {
        const selectedOptions = this.optionSelects.map((select) => select.value);

        if (selectedOptions.some((value) => !value)) {
          this.setButtonState(false, this.dataset.chooseSize);
          this.variantInput.value = '';
          return null;
        }

        const variant = this.variants.find(
          (candidate) =>
            candidate.options.length === selectedOptions.length &&
            candidate.options.every((option, index) => option === selectedOptions[index])
        );

        this.applyVariant(variant);
        return variant;
      }

      setVariantById(variantId) {
        const variant = this.variants.find((candidate) => String(candidate.id) === String(variantId));
        if (!variant) {
          this.applyVariant(null);
          return;
        }

        variant.options.forEach((option, index) => {
          if (this.optionSelects[index]) this.optionSelects[index].value = option;
        });
        this.applyVariant(variant);
      }

      applyVariant(variant) {
        if (!variant) {
          this.variantInput.value = '';
          this.setButtonState(false, this.dataset.unavailable);
          return;
        }

        this.variantInput.value = variant.id;
        if (this.quantityInput) this.quantityInput.value = variant.minimum || 1;
        if (this.price) this.price.textContent = variant.price;
        this.setButtonState(variant.available, variant.available ? this.dataset.addToCart : this.dataset.soldOut);
      }

      setButtonState(enabled, text) {
        this.submitButton.disabled = !enabled;
        this.submitButton.toggleAttribute('aria-disabled', !enabled);
        if (this.submitButtonText && text) this.submitButtonText.textContent = text;
      }

      syncMainProductPicker(selectedOptions) {
        const variantPicker = this.mainProductInfo?.querySelector('variant-selects');
        if (!variantPicker) return;

        const controls = [];
        const groups = variantPicker.querySelectorAll('fieldset, .product-form__input--dropdown');

        groups.forEach((group, index) => {
          const value = selectedOptions[index];
          const select = group.querySelector('select');
          const optionName =
            select?.name?.match(/^options\[(.+)]$/)?.[1] || group.querySelector('legend')?.textContent || '';

          if (select) {
            select.value = value;
            Array.from(select.options).forEach((option) => {
              if (option.value === value) {
                option.setAttribute('selected', 'selected');
              } else {
                option.removeAttribute('selected');
              }
            });
            controls.push({ element: select, optionName });
            return;
          }

          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === value
          );
          if (radio) {
            radio.checked = true;
            controls.push({ element: radio, optionName });
          }
        });

        const sizeControl = controls.find(({ optionName }) => /size/i.test(optionName));
        (sizeControl || controls.at(-1))?.element.dispatchEvent(new Event('change', { bubbles: true }));
      }

      handleClose() {
        this.isDismissed = true;
        this.closeButton?.blur();
        this.hide();
      }

      handleScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          const currentScrollY = Math.max(0, window.scrollY);
          const delta = currentScrollY - this.lastScrollY;

          if (Math.abs(delta) >= 4) this.scrollDirection = delta > 0 ? 'down' : 'up';
          this.lastScrollY = currentScrollY;
          this.updateVisibility();
          this.scrollFrame = null;
        });
      }

      handleResize() {
        this.lastScrollY = Math.max(0, window.scrollY);
        this.updateVisibility();
      }

      updateVisibility() {
        const triggerBounds = this.mainSubmitButton?.getBoundingClientRect();
        const mainButtonHasPassed = Boolean(triggerBounds && triggerBounds.bottom < 0);
        const shouldShow =
          !this.isDismissed &&
          this.scrollDirection === 'down' &&
          this.lastScrollY > 100 &&
          mainButtonHasPassed &&
          !document.querySelector('cart-drawer.active');

        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }

      hide() {
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
        this.scrollDirection = 'none';
      }
    }
  );
}
